import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, LogOut, CalendarDays, ClipboardCheck, Inbox, ArrowLeft, NotebookPen, Plus, Pencil, Trash2, Sparkles } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { LogoMark } from "../components/Nav";

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-[#e5e5e7] p-6" data-testid="admin-stat-card">
      <span className="w-10 h-10 rounded-xl bg-[#f5f5f7] border border-[#e5e5e7] flex items-center justify-center text-[#0066cc]">
        <Icon size={18} />
      </span>
      <p className="mt-4 text-3xl font-semibold tracking-tight text-[#1d1d1f]">{value}</p>
      <p className="text-sm text-[#86868b] mt-1">{label}</p>
    </div>
  );
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/admin/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f5f7] flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-3xl border border-[#e5e5e7] shadow-[0_10px_40px_rgba(0,0,0,0.06)] p-8 sm:p-10" data-testid="admin-login-form">
        <div className="flex items-center gap-3">
          <LogoMark size={36} />
          <div>
            <p className="font-semibold text-[#1d1d1f]">Studio Dashboard</p>
            <p className="text-xs text-[#86868b]">Bookings, leads & messages</p>
          </div>
        </div>
        <div className="mt-8 flex flex-col gap-4">
          <input
            required type="email" placeholder="Email" data-testid="admin-login-email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            className="px-5 py-3.5 rounded-2xl border border-[#d2d2d7] text-[15px] outline-none focus:border-[#0066cc] transition-colors"
          />
          <input
            required type="password" placeholder="Password" data-testid="admin-login-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            className="px-5 py-3.5 rounded-2xl border border-[#d2d2d7] text-[15px] outline-none focus:border-[#0066cc] transition-colors"
          />
        </div>
        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
        <button
          type="submit" disabled={loading} data-testid="admin-login-submit"
          className="mt-6 w-full px-6 py-3.5 rounded-full bg-[#0066cc] text-white font-medium text-sm hover:bg-[#0077ed] transition-all flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading && <Loader2 size={16} className="animate-spin" />}
          {loading ? "Signing in..." : "Sign in"}
        </button>
        <Link to="/" className="mt-6 flex items-center justify-center gap-1.5 text-sm text-[#86868b] hover:text-[#1d1d1f] transition-colors">
          <ArrowLeft size={14} /> Back to website
        </Link>
      </form>
    </div>
  );
}

const TABS = [
  { id: "bookings", label: "Bookings", icon: CalendarDays },
  { id: "assessments", label: "Assessments", icon: ClipboardCheck },
  { id: "messages", label: "Messages", icon: Inbox },
  { id: "journal", label: "Journal", icon: NotebookPen },
];

function JournalEditor({ post, onClose, onSaved }) {
  const [form, setForm] = useState(post);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (post.id) await api.put(`/journal/${post.id}`, form);
      else await api.post("/journal", form);
      onSaved();
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };
  const field = "px-5 py-3 rounded-2xl border border-[#d2d2d7] text-[15px] outline-none focus:border-[#0066cc] transition-colors";
  return (
    <div className="fixed inset-0 z-[85] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        data-testid="journal-editor-form"
        className="bg-white rounded-3xl w-full max-w-xl p-8 max-h-[90vh] overflow-y-auto thin-scroll"
      >
        <p className="text-lg font-semibold text-[#1d1d1f]">{post.id ? "Edit note" : "New journal note"}</p>
        <div className="mt-6 flex flex-col gap-4">
          <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={field} data-testid="journal-editor-title" />
          <div className="grid sm:grid-cols-2 gap-4">
            <input required placeholder="Season tag (e.g. AW '26 Note)" value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} className={field} data-testid="journal-editor-season" />
            <input placeholder="Cover image URL (optional)" value={form.cover || ""} onChange={(e) => setForm({ ...form, cover: e.target.value })} className={field} />
          </div>
          <textarea required rows={2} placeholder="Excerpt (shown on the card)" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className={field + " resize-none"} />
          <textarea required rows={8} placeholder={"Full note — separate paragraphs with a blank line"} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={field + " resize-none"} data-testid="journal-editor-body" />
          <label className="flex items-center gap-3 text-sm text-[#1d1d1f]">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="w-4 h-4 accent-[#0066cc]" />
            Published (visible on the website)
          </label>
        </div>
        <div className="mt-6 flex gap-3">
          <button type="submit" disabled={busy} className="px-6 py-3 rounded-full bg-[#0066cc] text-white text-sm font-medium hover:bg-[#0077ed] transition-all flex items-center gap-2 disabled:opacity-60">
            {busy && <Loader2 size={15} className="animate-spin" />} {busy ? "Saving..." : "Save note"}
          </button>
          <button type="button" onClick={onClose} className="px-6 py-3 rounded-full border border-[#d2d2d7] text-sm font-medium hover:bg-[#f5f5f7]">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function JournalPanel({ posts, onChanged }) {
  const [editing, setEditing] = useState(null);
  const remove = async (id) => {
    if (!window.confirm("Delete this journal note?")) return;
    await api.delete(`/journal/${id}`);
    onChanged();
  };
  return (
    <div data-testid="admin-journal-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#86868b]">{posts.length} notes</p>
        <button
          data-testid="journal-new-btn"
          onClick={() => setEditing({ title: "", season: "", excerpt: "", body: "", cover: "", published: true })}
          className="px-4 py-2 rounded-full bg-[#0066cc] text-white text-sm font-medium hover:bg-[#0077ed] transition-all flex items-center gap-2"
        >
          <Plus size={15} /> New note
        </button>
      </div>
      <div className="mt-4 grid gap-3">
        {posts.length === 0 && <p className="py-10 text-center text-[#86868b] bg-white rounded-2xl">No notes yet — write the first seasonal note.</p>}
        {posts.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-[#e5e5e7] p-5 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="flex items-center gap-2 text-xs">
                <span className="px-2 py-0.5 rounded-full bg-[#f5f5f7] border border-[#e5e5e7] text-[#0066cc] font-semibold uppercase tracking-wide">{p.season}</span>
                {!p.published && <span className="text-[#b3543f] font-medium">Draft</span>}
              </p>
              <p className="mt-1.5 font-medium text-[#1d1d1f]">{p.title}</p>
              <p className="text-sm text-[#86868b] line-clamp-1">{p.excerpt}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button aria-label="Edit note" data-testid={`journal-edit-${p.id.slice(0, 8)}`} onClick={() => setEditing(p)} className="p-2 rounded-full hover:bg-[#f5f5f7] transition-colors"><Pencil size={15} /></button>
              <button aria-label="Delete note" data-testid={`journal-delete-${p.id.slice(0, 8)}`} onClick={() => remove(p.id)} className="p-2 rounded-full hover:bg-[#faf0f0] text-[#b3543f] transition-colors"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && <JournalEditor post={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />}
    </div>
  );
}

function CalendarCard() {
  const [status, setStatus] = useState(null);
  const [hint, setHint] = useState("");
  const load = () =>
    api.get("/admin/google/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ configured: false, connected: false }));
  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    if (params.get("google")) {
      window.history.replaceState({}, "", "/admin");
    }
  }, []);
  const connect = async () => {
    setHint("");
    try {
      const { data } = await api.get("/admin/google/auth-url");
      window.location.href = data.authorization_url;
    } catch (err) {
      setHint(formatApiErrorDetail(err.response?.data?.detail) || "Could not start the Google connection");
    }
  };
  const disconnect = async () => {
    await api.post("/admin/google/disconnect");
    load();
  };
  if (!status) return null;
  return (
    <div className="bg-white rounded-2xl border border-[#e5e5e7] p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="calendar-sync-card">
      <div>
        <p className="font-medium text-[#1d1d1f] text-sm flex items-center gap-2">
          <CalendarDays size={16} className="text-[#0066cc]" /> Google Calendar sync
        </p>
        {status.connected ? (
          <p className="text-[13px] text-[#3d7a3d] mt-1">Connected — new bookings are added to sundeepa.bamval@gmail.com's calendar automatically.</p>
        ) : status.configured ? (
          <p className="text-[13px] text-[#86868b] mt-1">One-time setup: connect the calendar so every booking lands in it automatically.</p>
        ) : (
          <p className="text-[13px] text-[#86868b] mt-1 max-w-xl">Every booking email already carries a one-click "Add to Google Calendar" invite. For fully automatic sync, add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to the server environment.</p>
        )}
        {hint && <p className="text-[13px] text-red-600 mt-1">{hint}</p>}
      </div>
      {status.configured && (
        <div className="flex gap-2 shrink-0">
          {status.connected ? (
            <button onClick={disconnect} data-testid="calendar-disconnect-btn" className="px-4 py-2 rounded-full border border-[#d2d2d7] text-sm font-medium hover:bg-[#f5f5f7] transition-all">Disconnect</button>
          ) : (
            <button onClick={connect} data-testid="calendar-connect-btn" className="px-4 py-2 rounded-full bg-[#0066cc] text-white text-sm font-medium hover:bg-[#0077ed] transition-all">Connect calendar</button>
          )}
        </div>
      )}
    </div>
  );
}

function AssistantCard() {
  const [model, setModel] = useState("gpt-5.4");
  const [models, setModels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    api.get("/admin/settings")
      .then(({ data }) => { setModel(data.chat_model); setModels(data.chat_models || []); })
      .catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.put("/admin/settings", { chat_model: model });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="bg-white rounded-2xl border border-[#e5e5e7] p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="assistant-model-card">
      <div>
        <p className="font-medium text-[#1d1d1f] text-sm flex items-center gap-2">
          <Sparkles size={16} className="text-[#0066cc]" /> AI Assistant model
        </p>
        <p className="text-[13px] text-[#86868b] mt-1 max-w-xl">
          Studio SB is powered by ChatGPT (OpenAI). Pick which model answers your visitors — changes apply instantly, no redeploy.
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <select
          value={model}
          onChange={(e) => setModel(e.target.value)}
          data-testid="assistant-model-select"
          className="px-4 py-2.5 rounded-xl border border-[#d2d2d7] text-sm outline-none focus:border-[#0066cc] bg-white"
        >
          {(models.length ? models : [model]).map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <button
          onClick={save}
          disabled={saving}
          data-testid="assistant-model-save"
          className="px-4 py-2.5 rounded-full bg-[#0066cc] text-white text-sm font-medium hover:bg-[#0077ed] transition-all disabled:opacity-60"
        >
          {saving ? "Saving..." : saved ? "Saved ✓" : "Save"}
        </button>
      </div>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [tab, setTab] = useState("bookings");
  const [data, setData] = useState({ bookings: [], assessments: [], messages: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, a, m, j] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/assessments"),
        api.get("/admin/messages"),
        api.get("/admin/journal"),
      ]);
      setData({ bookings: b.data, assessments: a.data, messages: m.data, journal: j.data });
    } catch {
      onLogout();
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f5f7]" data-testid="admin-dashboard">
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark size={30} />
            <p className="font-semibold text-[#1d1d1f]">Studio Dashboard</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-[#86868b]">{user.email}</span>
            <button
              onClick={onLogout} data-testid="admin-logout-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#d2d2d7] text-sm font-medium hover:bg-white transition-all"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
        <CalendarCard />
        <div className="mt-5 grid sm:grid-cols-3 gap-5">
          <StatCard icon={CalendarDays} label="Total bookings" value={data.bookings.length} />
          <StatCard icon={ClipboardCheck} label="Assessments taken" value={data.assessments.length} />
          <StatCard icon={Inbox} label="Messages" value={data.messages.length} />
        </div>

        <div className="mt-8 flex gap-2 border-b border-[#e5e5e7]" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                tab === t.id ? "border-[#0066cc] text-[#0066cc]" : "border-transparent text-[#86868b] hover:text-[#1d1d1f]"
              }`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 flex justify-center text-[#86868b]"><Loader2 className="animate-spin" /></div>
        ) : (
          <div className="mt-6 overflow-x-auto">
            {tab === "bookings" && (
              <table className="w-full text-sm bg-white rounded-2xl overflow-hidden" data-testid="admin-table-bookings">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-[#86868b] border-b border-[#e5e5e7]">
                    <th className="px-5 py-4">Client</th><th className="px-5 py-4">Contact</th>
                    <th className="px-5 py-4">Date · Slot</th><th className="px-5 py-4">Topic</th><th className="px-5 py-4">Payment</th><th className="px-5 py-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.bookings.length === 0 && <tr><td colSpan="6" className="px-5 py-10 text-center text-[#86868b]">No bookings yet.</td></tr>}
                  {data.bookings.map((b) => (
                    <tr key={b.id} className="border-b border-[#f5f5f7] last:border-0 hover:bg-[#f5f5f7]/60">
                      <td className="px-5 py-4"><p className="font-medium text-[#1d1d1f]">{b.name}</p><p className="text-xs text-[#86868b]">{b.company || "—"}</p></td>
                      <td className="px-5 py-4 text-[#86868b]"><p>{b.email}</p><p>{b.phone}</p></td>
                      <td className="px-5 py-4">{b.date}<br /><span className="text-xs text-[#86868b]">{b.slot} IST</span></td>
                      <td className="px-5 py-4 max-w-[220px]">{b.topic}{b.message && <p className="text-xs text-[#86868b] mt-1 line-clamp-2">{b.message}</p>}</td>
                      <td className="px-5 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${b.payment_status === "paid" ? "bg-[#eef6ee] text-[#3d7a3d]" : b.payment_status === "pending" ? "bg-[#fdf3e0] text-[#9a6b1f]" : "bg-[#f5f5f7] text-[#86868b]"}`}>
                          {b.payment_status === "paid" ? "Paid ₹7,000" : b.payment_status === "pending" ? "Payment due" : "Free"}
                        </span>
                      </td>
                      <td className="px-5 py-4"><span className="px-3 py-1 rounded-full bg-[#eef6ee] text-[#3d7a3d] text-xs font-medium capitalize">{b.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {tab === "assessments" && (
              <table className="w-full text-sm bg-white rounded-2xl overflow-hidden" data-testid="admin-table-assessments">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-[#86868b] border-b border-[#e5e5e7]">
                    <th className="px-5 py-4">Lead</th><th className="px-5 py-4">Brand</th>
                    <th className="px-5 py-4">Score</th><th className="px-5 py-4">Band</th><th className="px-5 py-4">Recommended</th><th className="px-5 py-4">When</th>
                  </tr>
                </thead>
                <tbody>
                  {data.assessments.length === 0 && <tr><td colSpan="6" className="px-5 py-10 text-center text-[#86868b]">No assessments yet.</td></tr>}
                  {data.assessments.map((a) => (
                    <tr key={a.id} className="border-b border-[#f5f5f7] last:border-0 hover:bg-[#f5f5f7]/60">
                      <td className="px-5 py-4"><p className="font-medium text-[#1d1d1f]">{a.name}</p><p className="text-xs text-[#86868b]">{a.email}</p></td>
                      <td className="px-5 py-4">{a.brand || "—"}</td>
                      <td className="px-5 py-4 font-semibold">{a.score}/100</td>
                      <td className="px-5 py-4">{a.band}</td>
                      <td className="px-5 py-4 max-w-[200px]">{a.recommendation}</td>
                      <td className="px-5 py-4 text-[#86868b] text-xs">{fmt(a.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {tab === "messages" && (
              <div className="grid gap-4" data-testid="admin-table-messages">
                {data.messages.length === 0 && <p className="py-10 text-center text-[#86868b] bg-white rounded-2xl">No messages yet.</p>}
                {data.messages.map((m) => (
                  <div key={m.id} className="bg-white rounded-2xl border border-[#e5e5e7] p-6">
                    <div className="flex items-center justify-between gap-4">
                      <p className="font-medium text-[#1d1d1f]">{m.name} <span className="text-[#86868b] font-normal text-sm">· {m.email}</span></p>
                      <p className="text-xs text-[#86868b]">{fmt(m.created_at)}</p>
                    </div>
                    <p className="mt-3 text-[15px] text-[#1d1d1f]/80 leading-relaxed">{m.message}</p>
                  </div>
                ))}
              </div>
            )}
            {tab === "journal" && <JournalPanel posts={data.journal || []} onChanged={load} />}
          </div>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  return user ? (
    <Dashboard
      user={user}
      onLogout={async () => {
        try { await api.post("/admin/logout"); } catch {}
        setUser(null);
      }}
    />
  ) : (
    <Login onLogin={setUser} />
  );
}
